import * as xlsx from 'xlsx';
import {
  timeMonth,
  timeDay,
  timeFormat,
  csv,
  nest,
  text,
  csvParseRows,
} from 'd3';

export const ENERGY_SOURCES = {
  solar: 'solar',
  ['wind']: 'wind',
  ['wind_offshore']: 'wind_offshore',
};

export const ZONES = {
  region: 'region',
  province: 'province',
  offshore: 'offshore',
};

// provincies NIS code https://nl.wikipedia.org/wiki/NIS-code
export const PROVINCES = {
  antwerpen: 1,
  ['vlaams-brabant']: 2,
  ['west-vlaanderen']: 3,
  ['oost-vlaanderen']: 4,
  limburg: 7,
};

const getProvinceName = (provinceId) => {
  const namesById = {
    1: 'Antwerpen',
    2: 'Vlaams-Brabant',
    3: 'West-Vlaanderen',
    4: 'Oost-Vlaanderen',
    7: 'Limburg',
  };

  return namesById[provinceId] || '';
};

export const TIME_SCALES = {
  hourly: 'hourly',
  monthly: 'monthly',
};

export const TYPES = {
  production: 'production',
  installedCapacity: 'installedCapacity',
  demand: 'demand',
};

export const getLabel = (key) => {
  // labels keys are maps values
  const labels = {
    solar: 'Zonnepanelen',
    ['wind']: 'Onshore windturbines',
    ['wind_offshore']: 'Offshore windturbines',
    region: 'Vlaanderen',
    province: 'Provincie…',
    offshore: 'Offshore',
    hourly: 'Uurlijks',
    monthly: 'Maandelijks',
    antwerpen: 'Antwerpen',
    limburg: 'Limburg',
    ['oost-vlaanderen']: 'Oost-Vlaanderen',
    ['vlaams-brabant']: 'Vlaams-Brabant',
    ['west-vlaanderen']: 'West-Vlaanderen',
  };
  const label = labels[key];
  if (!label) {
    console.warn(`no key ${key} in labels`);
    return key;
  }
  return label;
};

export const getTableLabel = (key, energySource) => {
  const sourceLabel = energySource
    ? getLabel(energySource)?.toLocaleLowerCase()
    : '';
  const labels = {
    [TYPES.production]: `Productie door ${sourceLabel} [MW]`,
    [TYPES.installedCapacity]: `Geïnstalleerde capaciteit van ${sourceLabel} [MW]`,
    [TYPES.demand]: `Verbruik [MWh]`,
  };

  const label = labels[key];
  if (!label) {
    console.warn(`no key ${key} in tableLabels`);
    return key;
  }
  return label;
};

export const getDisclaimers = (energySource) => {
  const preDisclaimer = energySource === ENERGY_SOURCES.solar ?
    [['De vermelde vermogens zijn omvormersvermogens (AC-vermogen)']] : [];
  const [firstDisclaimer, ...otherDisclaimers] = energySource === ENERGY_SOURCES.solar ?
    ['Omdat nieuwe installaties verschillende stappen moeten doorlopen, worden ze in vele gevallen pas enkele maanden na indienstname door de netbeheerders geregistreerd.', 'De geïnstalleerde vermogens van het recentste kwartaal zijn dus per definitie onvolledig en een onderschatting van de werkelijkheid, waardoor ook de data voor groenestroomproductie van de werkelijkheid kunnen afwijken.', 'Het gebruik van deze data is dan ook op eigen risico, noch het VEKA noch 3E kunnen verantwoordelijk gehouden worden voor enige schade ten gevolge van het gebruik van deze data.'] :
    energySource === ENERGY_SOURCES.wind_offshore ?
    ['Voor de elektriciteitsproductie uit offshore windturbines worden data gemonitord door Elia.', 'Het gebruik van deze data is op eigen risico, noch het VEKA noch 3E kunnen verantwoordelijk gehouden worden voor enige schade ten gevolge van het gebruik van deze data.'] :
    ['Voor de elektriciteitsproductie uit windturbines in Vlaanderen of per provincie worden data van gemonitorde windturbines geëxtrapoleerd.', 'De resultaten kunnen bijgevolg afwijken van de werkelijke productie.', 'Omdat nieuwe installaties verschillende stappen moeten doorlopen, worden ze in vele gevallen pas maanden na indienstname door de netbeheerders geregistreerd.', 'De geïnstalleerde vermogens van het recentste kwartaal zijn dus per definitie onvolledig en een onderschatting van de werkelijkheid, waardoor ook de data voor groenestroomproductie van de werkelijkheid kunnen afwijken.', 'Het gebruik van deze data is dan ook op eigen risico, noch het VEKA noch 3E kunnen verantwoordelijk gehouden worden voor enige schade ten gevolge van het gebruik van deze data.'];
  const otherLines = otherDisclaimers.map(l => [undefined, l]);
  return [...preDisclaimer, ['Disclaimer:', firstDisclaimer], ...otherLines];
}

const formatterDaily = timeFormat('%Y%m%d');
const formatterMonthly = timeFormat('%Y-%m');
const formatterYearly = timeFormat('%Y');

const isMonthlyFilter = (timeScale) => timeScale === TIME_SCALES.monthly;

const getRange = (start, end, timeScale) => {
  const isMonthly = isMonthlyFilter(timeScale);
  const time = isMonthly ? timeMonth : timeDay;

  // get range from start to end included
  const startDate = time.floor(new Date(start));
  const endDate = time.offset(time.floor(new Date(end)), +1);

  const range = time.range(startDate, endDate);
  const dates = isMonthly ? formatDatesYearly(range) : formatDatesDaily(range);

  return {
    timeScale,
    dates,
  };
};

const formatDatesDaily = (range) => {
  return range.map((date) => formatterDaily(date));
};

const formatDatesYearly = (range) => {
  const formatted = range.map((date) => formatterYearly(date));
  // remove duplicates
  return Array.from(new Set(formatted));
};

const formatInstalledCapacity = (timeScale) => (rows) => {
  // replace all times by 00:00:00
  const [date, time] = rows.datetime.split(' ');
  const [_, timezone] = time.split('+');
  const datetime = isMonthlyFilter(timeScale)
    ? formatterMonthly(new Date(rows.datetime))
    : `${date} 00:00:00+${timezone}`;

  return Object.entries(rows)
    .filter(([key]) => key !== 'datetime')
    .map((row) => {
      const [communeNIS, capacity] = row;
      const value = parseFloat(capacity);

      // file doesn't exist and D3 returns an html error page so we deal with it this way
      if (isNaN(value)) return;
      const province = parseInt(communeNIS.split('')[0], 10);

      return {
        ...row,
        datetime,
        type: TYPES.installedCapacity,
        province,
        value,
      };
    });
};

const formatProduction = (timeScale) => (rows) => {
  const datetime = isMonthlyFilter(timeScale)
    ? formatterMonthly(new Date(rows.datetime))
    : rows.datetime;
  return Object.entries(rows).map((row) => {
    const [communeNIS, capacity] = row;
    const province = parseInt(communeNIS.split('')[0], 10);

    const value = parseFloat(capacity);
    if (isNaN(value) || isNaN(province)) return;

    return {
      ...row,
      datetime,
      type: TYPES.production,
      province,
      value,
    };
  });
};

const formatConsumption = (timeScale) => (row) => {
  const [rowDateTime, consumption] = row;

  const datetime = isMonthlyFilter(timeScale)
    ? formatterMonthly(new Date(rowDateTime))
    : rowDateTime;
  const value = parseFloat(consumption);
  if (isNaN(value)) return;

  return [
    {
      ...row,
      datetime,
      type: TYPES.demand,
      value,
    },
  ];
};

const getInstalledCapacityPromise = (energy, range) => {
  const { dates, timeScale } = range;
  const rootFolder = isMonthlyFilter(timeScale) ? 'historical' : 'realtime';
  const paths = dates.map(
    (date) => `./data/${rootFolder}/installed_capacity_${energy}_${date}.csv`,
  );
  return paths.map((path) => csv(path, formatInstalledCapacity(timeScale)));
};

const getProductionPromise = (energy, range) => {
  const { dates, timeScale } = range;
  const rootFolder = isMonthlyFilter(timeScale) ? 'historical' : 'realtime';
  const paths = dates.map(
    (date) => `./data/${rootFolder}/${rootFolder}_${energy}_${date}.csv`,
  );
  return paths.map((path) => csv(path, formatProduction(timeScale)));
};

const getConsumptionPromise = (range) => {
  const { dates, timeScale } = range;
  const isMonthly = isMonthlyFilter(timeScale);
  const rootFolder = isMonthly ? 'historical' : 'realtime';
  const fileName = isMonthly ? 'historical_load' : 'realtime_demand';
  const paths = dates.map(
    (date) => `./data/${rootFolder}/${fileName}_${date}.csv`,
  );
  return paths.map((path) =>
    text(path).then((v) => csvParseRows(v, formatConsumption(timeScale))),
  );
};

export const getRawData = async (filters) => {
  const { startDate, endDate, source, timeScale, zone } = filters;
  const range = getRange(startDate, endDate, timeScale);
  const installedCapacityPromise = getInstalledCapacityPromise(source, range);

  const productionPromise = getProductionPromise(source, range);

  const consumptionPromise =
    zone !== ZONES.province ? getConsumptionPromise(range) : [];

  return Promise.allSettled([
    ...installedCapacityPromise,
    ...productionPromise,
    ...consumptionPromise,
  ]);
};

export const filterData = (settledPromises, filters) => {
  const data = settledPromises
    .filter(
      ({ status, value }) =>
        // filtering out failed fetch and non-existing content
        status === 'fulfilled' || !value?.columns?.includes('<!DOCTYPE html>'),
    )
    .map((promise) => {
      return promise.value;
    })
    // filtering out empty values
    .filter((v) =>
      v?.filter((entry) => {
        return entry;
      }),
    )
    .map((v) => {
      // filtering only relevant province data if filter is set by province
      return filters.zone === ZONES.province
        ? v.map((entry) => {
            return entry.filter((row) => row?.province === filters.province);
          })
        : v;
    })
    .flat();

  return data;
};

// merge all communes of a selected zone (province or all region) to a single object
const combineZoneData = (entries) => {
  const row = entries.reduce(
    (accumulator, entry) => {
      if (!entry) return;
      const { value, type } = entry;

      // adding current entry value to accumulator value
      const rowValue = { [type]: accumulator[type] + value };
      return { ...accumulator, ...rowValue };
    },
    {
      [TYPES.production]: 0,
      [TYPES.demand]: 0,
      [TYPES.installedCapacity]: 0,
    },
  );
  return row;
};

let previousInstalledCapacity = 0;

const createRows = (data, { source, zone }) => {
  const productionLabel = getTableLabel(TYPES.production, source);
  const installedCapacityLabel = getTableLabel(TYPES.installedCapacity, source);
  const demandLabel = getTableLabel(TYPES.demand, source);

  return data
    .sort((a, b) => {
      return a.key > b.key;
    })
    .map((row) => {
      const {
        production,
        installedCapacity: currentInstalledCapacity,
        demand,
      } = row.value;

      // installed capacity data are only available once per day,
      // so to repeat them on every hour of the day (when "hourly" selected)
      // we reuse the previous hour data
      let installedCapacity =
        currentInstalledCapacity || previousInstalledCapacity;
      previousInstalledCapacity = installedCapacity;

      return {
        DateTime: row.key,
        [productionLabel]: production,
        [installedCapacityLabel]: installedCapacity,
        ...(zone !== ZONES.province ? { [demandLabel]: demand } : {}),
      };
    });
};

export const formatData = (json, filters) => {
  // group by datetime
  const data = nest()
    .key((k) => k?.datetime)
    .rollup(combineZoneData)
    .entries(json.flat())
    .filter((d) => {
      return d.key !== 'undefined';
    });

  const rows = createRows(data, filters);

  return rows;
};

export const getFileName = ({
  source,
  zone,
  province,
  timeScale,
  startDate,
  endDate,
}) => {
  const region =
    zone === ZONES.region ? getLabel(ZONES.region) : getProvinceName(province);
  return `Stroomvoorspeller_${getLabel(source)}_${region}_${getLabel(
    timeScale,
  )}_${startDate}_${endDate}`;
};

export const getSheetName = ({ source, zone, province }) => {
  if (source === ENERGY_SOURCES.wind_offshore) {
    return getLabel(ENERGY_SOURCES.wind_offshore);
  }
  if (zone === ZONES.region) {
    return 'Vlaanderen';
  }
  return getProvinceName(province);
};

export const downloadData = (rows, filters) => {

  if (rows.length === 0) {
    alert('Data for this period has not been published yet.');
    return;
  }
  const name = getSheetName(filters);

  const DATE_TIME_COL_WIDTH = 25; // 25 characters
  const fileName = getFileName(filters);
  const fileExtension = '.xlsx';
  const dateFormatter = timeFormat('%d/%m/%Y');
  const period = `Periode: ${dateFormatter(
    new Date(filters.startDate),
  )} tot ${dateFormatter(new Date(filters.endDate))}`;
  const dateLabel = `Tijdstip: Geconsulteerd op ${dateFormatter(new Date())}`;

  const wb = xlsx.utils.book_new();

  const ws = xlsx.utils.json_to_sheet([]);

  xlsx.utils.sheet_add_aoa(ws, [[period]], {
    origin: 'A1',
  });

  xlsx.utils.sheet_add_aoa(ws, [[`Regio: ${name}`]], {
    origin: 'A2',
  });

  xlsx.utils.sheet_add_aoa(ws, [[dateLabel]], {
    origin: 'A3',
  });
  const disclaimers = getDisclaimers(filters.source);

  xlsx.utils.sheet_add_aoa(ws, disclaimers, {
    origin: 'A4',
  });

  // add content with headers starting on row 6, as per model, cfr. DM-18
  xlsx.utils.sheet_add_json(ws, rows, { origin: `A${5+disclaimers.length}` });
  console.log({ rows });

  // sizing dateTime column based based its content
  ws['!cols'] = [{ wch: DATE_TIME_COL_WIDTH }];
  // sizing other columns based on their header
  // starting at index 1 (col B) to skip dateTime one
  for (let i = 1; i < Object.keys(rows[0]).length; i++) {
    const col_name = xlsx.utils.encode_col(i);
    const cell = ws[`${col_name}4`];
    const wch = cell?.v?.length ?? 10;
    ws['!cols'].push({ wch });
  }
  xlsx.utils.book_append_sheet(wb, ws, name);

  xlsx.writeFile(wb, `${fileName}${fileExtension}`);
};
